import CustomButton from '@/components/atoms/CustomButton';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import useGetInfiniteNotifications from '@/hooks/notifications/useGetInfiniteNotifications';
import NotificationListItem from './NotificationListItem';
import NotificationsLoadingSkeleton from './NotificationsLoadingSkeleton';
import svgIcons from '@/assets/svg/icons';
import { TNotification } from '@/types/Notification.types';
import useReadAllNotifications from '@/hooks/notifications/useReadAllNotifications';

const BOTTOM_SCROLL_DELTA_HEIGHT = 100;

interface Props {
  onDrawerClose: () => void;
  externalIsDrawerOpen?: boolean;
}

const AllNotificationsDrawer = ({ onDrawerClose, externalIsDrawerOpen }: Props) => {
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose } = useDisclosure();

  const localOnClose = useCallback(() => {
    onClose();
    onDrawerClose();
  }, [onClose, onDrawerClose]);

  const btnRef = useRef(null);

  const {
    data: notifications,
    isLoading,
    hasNextPage,
    refetch,
    isRefetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetInfiniteNotifications({ enabled: isDrawerOpen });

  const { mutate: markAllNotificationsAsRead, isPending: isReadAllPending } = useReadAllNotifications(refetch);

  useEffect(() => {
    if (externalIsDrawerOpen) onDrawerOpen();
  }, [externalIsDrawerOpen]);

  // notifications state where we'll update the isRead status
  const [localNotifications, setLocalNotifications] = useState(notifications?.length ? notifications : []);

  const [wasFetchingNextPage, setWasFetchingNextPage] = useState(false);
  const [wasRefetching, setWasRefetching] = useState(false);

  // when we start fetching the next page, we set wasFetchingNextPage to `true`
  // that way, when we STOP fetching next page and we WERE fetching next page (wasFetchingNextPage is true)
  // we know that we can update localNotifications
  useEffect(() => {
    if (!localNotifications || localNotifications.length === 0) {
      setLocalNotifications(notifications);
    }

    if (isFetchingNextPage) {
      setWasFetchingNextPage(true);
    } else {
      if (wasFetchingNextPage) {
        setLocalNotifications((prev) => [...prev, ...notifications.slice(prev.length)]);
        setWasFetchingNextPage(false);
      }
    }

    if (isRefetching) {
      setWasRefetching(true);
    } else {
      if (wasRefetching) {
        setLocalNotifications(notifications);
        setWasRefetching(false);
      }
    }
  }, [isFetchingNextPage, isRefetching, wasFetchingNextPage, notifications]);

  const drawerBodyRef = useRef<HTMLDivElement>(null);

  const onScroll = useCallback(
    (scrollEvent: UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = scrollEvent.target as HTMLDivElement;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - BOTTOM_SCROLL_DELTA_HEIGHT;

      if (isNearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // if screenNotFullyCoveredWithNotifications - fetch one more page
  useEffect(() => {
    if (isDrawerOpen && !isLoading && !isFetchingNextPage) {
      // wait/timeout of one second to let the drawer fully open and allow the notifications to be displayed
      setTimeout(() => {
        if (drawerBodyRef.current) {
          const { scrollHeight, clientHeight } = drawerBodyRef.current;

          const screenNotFullyCoveredWithNotifications = scrollHeight < clientHeight + BOTTOM_SCROLL_DELTA_HEIGHT;

          if (screenNotFullyCoveredWithNotifications) {
            fetchNextPage();
          }
        }
      }, 1000);
    }
  }, [isDrawerOpen, isLoading, isFetchingNextPage]);

  const onNotificationView = useCallback((notification: TNotification) => {
    setLocalNotifications((prev) => {
      const viewedNotification = { ...notification, isRead: true };
      const viewedNotificationIndex = prev.findIndex((notification) => notification._id === viewedNotification._id);

      const newLocalNotifications = [...prev];
      newLocalNotifications.splice(viewedNotificationIndex, 1, viewedNotification);

      return newLocalNotifications;
    });
  }, []);

  const isFetching = isLoading || isRefetching || isFetchingNextPage || isReadAllPending;

  return (
    <VStack>
      <CustomButton
        ref={btnRef}
        onClick={onDrawerOpen}
        label="VIEW ALL NOTIFICATIONS"
        labelHoverColor="extra.black"
        labelColor="text.mediumBlue"
        labelStyle={{ fontWeight: 800, fontSize: '12px' }}
        isTransparent
      />

      <Drawer size={'sm'} isOpen={isDrawerOpen} placement="right" onClose={localOnClose} finalFocusRef={btnRef}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader paddingTop={2} paddingBottom={0}>
            <HStack>
              <Text width={'100%'} textAlign={'start'} variant={'loraMedium'} color={'text.darkBlue'}>
                All Notifications
              </Text>
              <CustomButton
                label="Mark all as Read"
                iconBeforeLabelSvgPath={svgIcons.check}
                iconBeforeLabelSvgSize={12}
                labelHoverColor="extra.black"
                onClick={() => markAllNotificationsAsRead()}
                isTransparent
              />
            </HStack>
          </DrawerHeader>

          <DrawerBody ref={drawerBodyRef} paddingY={0} onScroll={onScroll}>
            {localNotifications?.map((notification) => (
              <NotificationListItem key={notification._id} notification={notification} onViewed={onNotificationView} />
            ))}
            {!isFetching && localNotifications?.length === 0 && (
              <Text variant={'urbanistSemiBold'} marginY={'30px'} color={'text.mediumBlue'}>
                No Notifications to display.
              </Text>
            )}
            {isFetching && <NotificationsLoadingSkeleton count={10} />}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </VStack>
  );
};

export default AllNotificationsDrawer;
